import { useCallback, useEffect, useMemo, useRef } from 'react'
import {
  PRMarinaFileArtboard,
  usePRFileSections,
} from '@sketch-hq/sketch-web-renderer'

import { EntityOverlayProps } from '../../../../canvas/components/EntityOverlay'
import { useSelectedFrame } from '../../../../canvas/components/SelectedFrameContext'
import { usePlaceDraftAnnotation } from '../../../../annotations/hooks'
import { useStableHandler, noop } from '@sketch/utils'
import {
  useNavigateToFrame,
  useNavigateToSection,
} from '../../../../canvas/hooks'
import { useIsPreviewView } from '../../../views/PreviewView/PreviewContext'

type FramesDict = Record<string, HTMLDivElement | null>
const EMPTY_ARRAY = [] as const

/**
 * Hook that manages view mode state and provides frame refs for DOM tracking.
 * Uses SelectedFrameContext to share the selected frame UUID with other components.
 */
export function useViewModeState() {
  const framesRefs = useRef<FramesDict>({})
  const selectedFrameContext = useSelectedFrame()

  // Fall back to no-op if context is not available
  const selectedArtboardUUID = selectedFrameContext?.selectedFrameUUID ?? null
  const setSelectedArtboardUUID =
    selectedFrameContext?.setSelectedFrameUUID ?? noop

  return {
    framesRefs,
    selectedArtboardUUID,
    setSelectedArtboardUUID,
  }
}

/**
 * Hook that returns props specific to view mode for the ArtboardOverlay component.
 */
export function useViewModeOverlayProps(
  frame: PRMarinaFileArtboard,
  selectedArtboardUUID: string | null,
  setSelectedArtboardUUID: (uuid: string | null) => void,
  isPlacingDraftAnnotation: boolean,
  isSection: boolean
): Partial<EntityOverlayProps> {
  const navigateToFrame = useNavigateToFrame()
  const navigateToSection = useNavigateToSection()
  const isPreviewView = useIsPreviewView()

  // The SECTION route only exists in the main Share flow - Sections viewed
  // within a Preview keep navigating like a regular frame.
  const navigateToEntity =
    isSection && !isPreviewView ? navigateToSection : navigateToFrame

  const isSelected = selectedArtboardUUID === frame.objectId

  const handleSelectFrame = useStableHandler(() => {
    if (!isSelected) {
      setSelectedArtboardUUID(frame.objectId)
    }
  })

  const doubleClickHandler = useStableHandler(() => {
    navigateToEntity(frame.objectId, 'doubleClick')
  })

  const handleCTAClick = useStableHandler(() => {
    if (isSelected) {
      navigateToEntity(frame.objectId, 'CTA')
    }
  })

  if (isPlacingDraftAnnotation) {
    return {
      selected: isSelected,
      disableAutoHover: true,
    }
  }

  return {
    selected: isSelected,
    disableAutoHover: false,
    // Note: We are setting handlers to undefined when not needed as having a value
    // or not controls if the cursor is a pointer or not in the FrameOverlay component.
    onOverlayClick: !isSelected ? handleSelectFrame : undefined,
    onOverlayDoubleClick: doubleClickHandler,
    onCtaClick: isSelected ? handleCTAClick : undefined,
  }
}

/**
 * Hook that handles global events for frame overlays in view mode (keyboard shortcuts and click outside).
 * Only active when not in inspector mode.
 */
export function useViewModeGlobalEvents(
  framesRefs: React.RefObject<FramesDict>,
  selectedArtboardUUID: string | null,
  setSelectedArtboardUUID: (uuid: string | null) => void,
  isInspectorActive: boolean
) {
  const navigateToFrame = useNavigateToFrame()
  const navigateToSection = useNavigateToSection()
  const isPreviewView = useIsPreviewView()
  const sections = usePRFileSections() ?? EMPTY_ARRAY
  const [isPlacingDraftAnnotation] = usePlaceDraftAnnotation() || []

  // The selected entity can be a Section as well as a frame; Sections navigate
  // through the PAGE_SECTION route instead of the FRAME one (except in Preview,
  // where they behave like frames). Mirrors useViewModeOverlayProps.
  const sectionObjectIds = useMemo(
    () => new Set(sections.map(section => section.objectId)),
    [sections]
  )

  /**
   * These function covers these scenarios:
   * - When an entity is selected and the user is writing a comment, when the
   *   user clicks `⌘ + Enter` it should publish the comment, not navigate to
   *   the entity
   * - When an entity is selected and the user is writing a comment, when the
   *   user clicks `Enter` it should add a new line, not navigate to the entity
   */
  const goToEntityGuard: typeof navigateToFrame = useCallback(
    (uuid, actionType) => {
      if (isPlacingDraftAnnotation) {
        return
      }

      if (!isPreviewView && sectionObjectIds.has(uuid)) {
        navigateToSection(uuid, actionType)
      } else {
        navigateToFrame(uuid, actionType)
      }
    },
    [
      navigateToFrame,
      navigateToSection,
      isPreviewView,
      sectionObjectIds,
      isPlacingDraftAnnotation,
    ]
  )

  const handleWindowKeyPress = useStableHandler((event: KeyboardEvent) => {
    if (!selectedArtboardUUID) {
      return
    }

    if (event.key === 'Enter' && document.activeElement) {
      goToEntityGuard(selectedArtboardUUID!, 'keyPressEnter')
    } else if (event.key === 'Escape') {
      setSelectedArtboardUUID(null)
    }
  })

  // Handle click outside to deselect the currently selected frame
  const handleWindowClick = useStableHandler(
    (event: MouseEvent | TouchEvent) => {
      // Checking if the click on the overlay was inside one of the artboards,
      // If not, we deselect the selected artboard
      for (const frameId in framesRefs.current) {
        if (framesRefs.current[frameId]?.contains(event.target as Node)) {
          return
        }
      }

      // if zoom or pages dropdown is clicked, we don't want to deselect the artboard
      const zoomDropdown = document.querySelector('[data-testid="zoom-toggle"]')
      const pagesDropdown = document.querySelector(
        '[data-testid="pages-selector-trigger"]'
      )

      const isZoomDropdownClicked = zoomDropdown?.contains(event.target as Node)
      const isPagesDropdownClicked = pagesDropdown?.contains(
        event.target as Node
      )

      if (isZoomDropdownClicked || isPagesDropdownClicked) {
        return
      }

      if (selectedArtboardUUID) setSelectedArtboardUUID(null)
    }
  )

  useEffect(() => {
    // Only attach event listeners in view mode
    if (isInspectorActive || !selectedArtboardUUID) {
      return
    }

    window.document.body.addEventListener('keydown', handleWindowKeyPress)
    window.document.body.addEventListener('click', handleWindowClick)

    return () => {
      window.document.body.removeEventListener('keydown', handleWindowKeyPress)
      window.document.body.removeEventListener('click', handleWindowClick)
    }
  }, [
    handleWindowClick,
    handleWindowKeyPress,
    selectedArtboardUUID,
    isInspectorActive,
  ])
}
